import React, { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Plus, Trash2, Upload } from "lucide-react";

/**
 * Content Management Tab
 * Structure: sections → fields. Each field is text, textarea, image, bool, list, or object.
 */

// Schema drives the entire editor. Adding new fields = adding to this schema.
const SCHEMA = [
    {
        section: "global",
        title: "Global · Logo · Halaal · Contact",
        script: "Site-wide",
        fields: [
            { key: "logo_url", label: "Site logo (used in header + footer)", type: "image" },
            { key: "halaal_logo_url", label: "Halaal Certified logo", type: "image" },
            { key: "halaal_caption", label: "Halaal caption", type: "text" },
            { key: "halaal_subcaption", label: "Halaal sub-caption", type: "text" },
            { key: "show_halaal_header", label: "Show halaal badge in header (top-right)", type: "bool" },
            { key: "show_halaal_hero", label: "Show halaal badge in home hero section", type: "bool" },
            { key: "phone_display", label: "Phone (display format)", type: "text" },
            { key: "phone_tel", label: "Phone (tel: link — international format, e.g. +27686760224)", type: "text" },
            { key: "whatsapp_number", label: "WhatsApp number (digits only, no +)", type: "text" },
            { key: "address_line1", label: "Address line 1", type: "text" },
            { key: "address_line2", label: "Address line 2", type: "text" },
            { key: "hours_lines", label: "Trading hours (one line per row)", type: "list-text" },
        ],
    },
    {
        section: "home",
        title: "Home Page",
        script: "Landing",
        fields: [
            { key: "hero_image_url", label: "Hero background image", type: "image" },
            { key: "hero_script", label: "Script tagline (small script text)", type: "text" },
            { key: "hero_headline", label: "Main headline (H1)", type: "textarea" },
            { key: "hero_subheading", label: "Sub-heading paragraph", type: "textarea" },
            { key: "hero_cta_primary", label: "Primary button label", type: "text" },
            { key: "hero_cta_secondary", label: "Secondary button label", type: "text" },
            { key: "hero_pickup_text", label: "Pickup line (under buttons)", type: "text" },

            { key: "value_strip", label: "Value strip (4 items)", type: "list-obj",
              fields: [{key: "label", label: "Label"}] },

            { key: "categories_script", label: "Featured categories script", type: "text" },
            { key: "categories_title", label: "Featured categories heading", type: "text" },
            { key: "category_tiles", label: "Category tiles (6)", type: "list-obj",
              fields: [
                { key: "slug", label: "Slug (link target — do not translate)" },
                { key: "name", label: "Display name" },
                { key: "img_url", label: "Tile image", type: "image" },
              ]},

            { key: "specials_script", label: "Specials banner script", type: "text" },
            { key: "specials_title", label: "Specials banner heading", type: "text" },
            { key: "specials_body", label: "Specials banner body", type: "textarea" },
            { key: "specials_cta", label: "Specials CTA button", type: "text" },
            { key: "specials_rotation_images", label: "Daily rotation images (4)", type: "list-image" },

            { key: "contact_script", label: "Contact snippet script", type: "text" },
            { key: "contact_title", label: "Contact snippet heading", type: "text" },
        ],
    },
    {
        section: "menu_page",
        title: "Menu Page",
        script: "Order Online",
        fields: [
            { key: "script", label: "Script tagline", type: "text" },
            { key: "title", label: "Page heading (H1)", type: "text" },
            { key: "subtitle", label: "Sub-heading paragraph", type: "textarea" },
        ],
    },
    {
        section: "specials_page",
        title: "Specials Page",
        script: "Specials & Bundles",
        fields: [
            { key: "script", label: "Script tagline", type: "text" },
            { key: "title", label: "Page heading (H1)", type: "text" },
            { key: "subtitle", label: "Sub-heading paragraph", type: "textarea" },
        ],
    },
    {
        section: "about_page",
        title: "About Page",
        script: "Our Story",
        fields: [
            { key: "hero_image_url", label: "About hero image", type: "image" },
            { key: "script", label: "Script tagline", type: "text" },
            { key: "title_line1", label: "Title line 1", type: "text" },
            { key: "title_line2", label: "Title line 2", type: "text" },
            { key: "paragraphs", label: "Story paragraphs", type: "list-text" },
            { key: "highlights", label: "Highlight cards", type: "list-obj",
              fields: [
                { key: "title", label: "Title" },
                { key: "body", label: "Body" },
              ]},
            { key: "cta", label: "CTA button label", type: "text" },
        ],
    },
    {
        section: "contact_page",
        title: "Contact Page",
        script: "Find Us",
        fields: [
            { key: "script", label: "Script tagline", type: "text" },
            { key: "title", label: "Page heading (H1)", type: "text" },
            { key: "subtitle", label: "Sub-heading paragraph", type: "textarea" },
            { key: "form_script", label: "Contact form script", type: "text" },
            { key: "form_title", label: "Contact form heading", type: "text" },
        ],
    },
    {
        section: "footer",
        title: "Footer",
        script: "Footer",
        fields: [
            { key: "tagline", label: "Footer tagline (under logo)", type: "textarea" },
            { key: "visit_heading", label: "Visit column heading", type: "text" },
            { key: "hours_heading", label: "Hours column heading", type: "text" },
            { key: "explore_heading", label: "Explore column heading", type: "text" },
        ],
    },
];

// ---------- Small inputs ----------
const inputCls = "w-full bg-brand-ink border border-brand-line px-3 py-2 rounded-sm text-brand-cream focus:outline-none focus:border-brand-olive text-sm";

function TextInput({ value, onChange, ...rest }) {
    return <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function TextArea({ value, onChange, rows = 3 }) {
    return <textarea rows={rows} className={`${inputCls} resize-none`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function BoolField({ value, onChange, label }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={!!value} onCheckedChange={onChange} />
            <span className="text-sm text-brand-cream">{label}</span>
        </label>
    );
}

function ImageField({ value, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const preview = value && (value.startsWith("http") || value.startsWith("/") ? value : `${BACKEND}${value}`);

    const upload = async (file) => {
        if (!file || !file.type.startsWith("image/")) {
            if (file) toast.error("Please pick an image file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await client.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(`${BACKEND}${data.url}`);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="flex items-center gap-3">
            <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    upload(e.dataTransfer.files?.[0]);
                }}
                className={`relative w-24 h-24 border-2 border-dashed rounded-sm overflow-hidden cursor-pointer transition-colors flex-shrink-0 ${
                    dragOver ? "border-brand-olive bg-brand-olive/10" : "border-brand-line hover:border-brand-gold"
                }`}
            >
                {preview ? (
                    <img src={preview} alt="" className="w-full h-full object-contain bg-brand-ink" />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-brand-mute p-2 text-center">
                        <Upload size={14} className="mb-1" />Drop / pick
                    </div>
                )}
                {uploading && <div className="absolute inset-0 bg-brand-ink/80 flex items-center justify-center text-[10px] text-brand-gold">…</div>}
                <input ref={inputRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            <div className="flex-1">
                <input
                    className={inputCls}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Image URL (or drop image on the left)"
                />
                <p className="text-[10px] text-brand-mute mt-1">Paste a URL, or drag/drop / click the box to upload from your computer.</p>
            </div>
        </div>
    );
}

function ListTextField({ value, onChange }) {
    const list = Array.isArray(value) ? value : [];
    const set = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
    const add = () => onChange([...list, ""]);
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-2">
            {list.map((v, i) => (
                <div key={i} className="flex gap-2">
                    <input className={inputCls} value={v} onChange={(e) => set(i, e.target.value)} />
                    <button onClick={() => remove(i)} className="text-brand-mute hover:text-brand-gold px-2"><Trash2 size={14} /></button>
                </div>
            ))}
            <button onClick={add} className="text-xs text-brand-gold inline-flex items-center gap-1 hover:underline">
                <Plus size={12} /> Add line
            </button>
        </div>
    );
}

function ListImageField({ value, onChange }) {
    const list = Array.isArray(value) ? value : [];
    const set = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
    const add = () => onChange([...list, ""]);
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-3">
            {list.map((v, i) => (
                <div key={i} className="flex gap-2 items-start">
                    <div className="flex-1"><ImageField value={v} onChange={(nv) => set(i, nv)} /></div>
                    <button onClick={() => remove(i)} className="text-brand-mute hover:text-brand-gold px-2 mt-2"><Trash2 size={14} /></button>
                </div>
            ))}
            <button onClick={add} className="text-xs text-brand-gold inline-flex items-center gap-1 hover:underline">
                <Plus size={12} /> Add image
            </button>
        </div>
    );
}

function ListObjectField({ value, onChange, fields }) {
    const list = Array.isArray(value) ? value : [];
    const setItem = (i, key, v) => onChange(list.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)));
    const add = () => onChange([...list, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-3">
            {list.map((obj, i) => (
                <div key={i} className="border border-brand-line p-3 rounded-sm space-y-2 relative">
                    <button onClick={() => remove(i)} className="absolute top-2 right-2 text-brand-mute hover:text-brand-gold"><Trash2 size={12} /></button>
                    {fields.map((f) => (
                        <div key={f.key}>
                            <p className="text-[10px] uppercase tracking-wider text-brand-mute mb-1">{f.label}</p>
                            {f.type === "image" ? (
                                <ImageField value={obj[f.key]} onChange={(v) => setItem(i, f.key, v)} />
                            ) : (
                                <input className={inputCls} value={obj[f.key] ?? ""} onChange={(e) => setItem(i, f.key, e.target.value)} />
                            )}
                        </div>
                    ))}
                </div>
            ))}
            <button onClick={add} className="text-xs text-brand-gold inline-flex items-center gap-1 hover:underline">
                <Plus size={12} /> Add
            </button>
        </div>
    );
}

// ---------- Main ContentTab ----------
export default function ContentTab({ onSaved, sections }) {
    const schema = sections ? SCHEMA.filter((s) => sections.includes(s.section)) : SCHEMA;
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState(schema[0].section);

    useEffect(() => {
        client.get("/content").then((r) => setContent(r.data)).catch(() => {});
    }, []);

    if (!content) return <p className="text-brand-mute">Loading content…</p>;

    const setField = (section, key, val) => {
        setContent((c) => ({ ...c, [section]: { ...(c[section] || {}), [key]: val } }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await client.patch("/admin/content", content);
            toast.success("Saved. The live site now shows your changes.");
            if (onSaved) await onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const currentSection = schema.find((s) => s.section === activeSection);
    const sectionData = content[activeSection] || {};

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
            {/* Sidebar */}
            <aside className="lg:sticky lg:top-24 h-fit">
                <nav className="flex flex-col gap-1 border-l border-brand-line">
                    {schema.map((s) => (
                        <button
                            key={s.section}
                            onClick={() => setActiveSection(s.section)}
                            data-testid={`content-tab-${s.section}`}
                            className={`text-left text-sm px-4 py-2 border-l-2 -ml-[1px] transition-colors ${
                                activeSection === s.section
                                    ? "border-brand-gold text-brand-gold"
                                    : "border-transparent text-brand-cream/70 hover:text-brand-cream"
                            }`}
                        >
                            {s.title}
                        </button>
                    ))}
                </nav>
                <button
                    data-testid="content-save-all"
                    onClick={save}
                    disabled={saving}
                    className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 text-sm"
                >
                    <Save size={14} /> {saving ? "Saving…" : "Save all changes"}
                </button>
                <p className="text-[10px] text-brand-mute mt-2 leading-tight">
                    Changes save the entire content set at once. Refresh the customer site to see updates.
                </p>
            </aside>

            {/* Editor */}
            <div className="space-y-6">
                <div>
                    <p className="font-script text-brand-gold text-2xl">{currentSection.script}</p>
                    <h3 className="font-display text-2xl">{currentSection.title}</h3>
                </div>

                <div className="space-y-5">
                    {currentSection.fields.map((f) => (
                        <div key={f.key} className="border border-brand-line rounded-sm p-4 bg-brand-charcoal">
                            <p className="text-xs uppercase tracking-wider text-brand-gold mb-2">{f.label}</p>
                            {f.type === "text" && <TextInput value={sectionData[f.key]} onChange={(v) => setField(activeSection, f.key, v)} />}
                            {f.type === "textarea" && <TextArea value={sectionData[f.key]} onChange={(v) => setField(activeSection, f.key, v)} rows={f.rows || 3} />}
                            {f.type === "image" && <ImageField value={sectionData[f.key]} onChange={(v) => setField(activeSection, f.key, v)} />}
                            {f.type === "bool" && <BoolField value={sectionData[f.key]} label={f.label} onChange={(v) => setField(activeSection, f.key, v)} />}
                            {f.type === "list-text" && <ListTextField value={sectionData[f.key]} onChange={(v) => setField(activeSection, f.key, v)} />}
                            {f.type === "list-image" && <ListImageField value={sectionData[f.key]} onChange={(v) => setField(activeSection, f.key, v)} />}
                            {f.type === "list-obj" && <ListObjectField value={sectionData[f.key]} onChange={(v) => setField(activeSection, f.key, v)} fields={f.fields} />}
                        </div>
                    ))}
                </div>

                <button
                    onClick={save}
                    disabled={saving}
                    className="btn-primary inline-flex items-center gap-2"
                >
                    <Save size={14} /> {saving ? "Saving…" : "Save all changes"}
                </button>
            </div>
        </div>
    );
}
